import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { styles, colors, fmtDate, fmtMoney, useIsMobile, mobile } from '../components/UI';

// Rentalman customer master view. Dead-customer mode is the killer feature
// here -- shows everyone Rentalman calls Active that the CRM has no recent
// touch on. Admin/leadership/supervisor can also assign customers to a rep
// to chase (CRM-side assignment, distinct from Rentalman's commission ID).
export default function Customers() {
  const { user } = useAuth();
  const canAssign = isAdminLike(user) || user.role === 'supervisor';
  // Spend is sales data; the server strips it for ops.
  const showSpend = user.role !== 'ops';

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [reps, setReps] = useState([]);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({
    q: '',
    status: 'A',          // default Active -- don't clutter view with deleted rows
    owner_id: '',         // '', 'mine', 'unassigned', or numeric user id
    location_code: '',
    dead: false
  });
  const [selected, setSelected] = useState(new Set());
  const [assignTarget, setAssignTarget] = useState(null);   // { row } or { bulk: true }
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();

  const reload = () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.status) params.status = filter.status;
    if (filter.owner_id) params.owner_id = filter.owner_id;
    if (filter.location_code) params.location_code = filter.location_code;
    if (filter.dead) params.dead = 'true';
    api.listCustomers(params).then(setRows).catch(e => setError(e.message));
    api.customerStats().then(setStats).catch(() => {});
  };

  useEffect(reload, [filter.q, filter.status, filter.owner_id, filter.location_code, filter.dead]);

  // Load the reps list for every non-ops user. AMs need it to filter the
  // shared Rentalman master list by peer ("show me Donald's customers")
  // since they now see all customers, not just their own.
  useEffect(() => {
    api.listReps().then(setReps).catch(() => {});
  }, [user.role]);

  // Clear selection whenever the filtered list changes (rows might disappear)
  useEffect(() => { setSelected(new Set()); }, [filter.q, filter.status, filter.owner_id, filter.location_code, filter.dead]);

  const locations = useMemo(
    () => [...new Set(rows.map(r => r.location_code).filter(Boolean))].sort(),
    [rows]
  );

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const doAssign = async ({ userId, notes }) => {
    if (!assignTarget) return;
    setBusy(true);
    try {
      if (assignTarget.bulk) {
        await api.bulkAssignCustomers({
          customer_ids: [...selected],
          user_id: userId,
          notes: notes || null
        });
      } else {
        await api.assignCustomer(assignTarget.row.id, {
          user_id: userId,
          notes: notes || null
        });
      }
      setAssignTarget(null);
      setSelected(new Set());
      reload();
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <h1 style={{ ...styles.h1, margin: 0 }}>
          Customers <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 14 }}>· Rentalman master · {rows.length} shown</span>
        </h1>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {stats && (
        <div style={styles.kpiGrid}>
          <Stat label="Total" value={stats.total} sub="customers in master" />
          <Stat label="Active" value={stats.active} sub={`${stats.suspended} suspended · ${stats.deleted} deleted`} />
          <Stat label="CRM-Assigned" value={stats.crm_assigned} sub="handed to a rep to chase" />
          <Stat label="Last Imported" value={stats.last_import_at ? fmtDate(stats.last_import_at) : 'Never'} sub="pulled daily from RentalMan" />
        </div>
      )}

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...styles.input, minWidth: 280, flex: '1 1 280px' }}
          placeholder="🔍  Search by name..."
          value={filter.q}
          onChange={e => setFilter({ ...filter, q: e.target.value })}
        />
        <select
          style={{ ...styles.input, width: 160 }}
          value={filter.status}
          onChange={e => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">All statuses</option>
          <option value="A">Active</option>
          <option value="B">B</option>
          <option value="S">Suspended</option>
          <option value="D">Deleted</option>
        </select>
        <select
          style={{ ...styles.input, width: 240 }}
          value={filter.owner_id}
          onChange={e => setFilter({ ...filter, owner_id: e.target.value })}
        >
          <option value="">All Account Mgrs</option>
          <option value="mine">— Mine —</option>
          <option value="unassigned">— Unassigned —</option>
          {reps.map(r => (
            <option key={r.id} value={r.id}>{r.full_name}</option>
          ))}
        </select>
        {locations.length > 0 && (
          <select
            style={{ ...styles.input, width: 160 }}
            value={filter.location_code}
            onChange={e => setFilter({ ...filter, location_code: e.target.value })}
          >
            <option value="">All locations</option>
            {locations.map(l => <option key={l} value={l}>Location {l}</option>)}
          </select>
        )}
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', borderRadius: 8,
          border: `1.5px solid ${filter.dead ? '#92400E' : '#E5E7EB'}`,
          background: filter.dead ? '#FEF3C7' : '#fff',
          color: filter.dead ? '#92400E' : '#374151',
          fontWeight: 600, fontSize: 13, cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={filter.dead}
            onChange={e => setFilter({ ...filter, dead: e.target.checked, status: e.target.checked ? 'A' : filter.status })}
            style={{ margin: 0 }}
          />
          💤 Dead customers only
        </label>
        {(filter.q || filter.owner_id || filter.location_code || filter.dead || filter.status !== 'A') && (
          <button style={styles.btnSecondary} onClick={() => setFilter({ q: '', status: 'A', owner_id: '', location_code: '', dead: false })}>Reset</button>
        )}
      </div>

      {/* Bulk action bar -- only visible when caller can assign AND rows selected */}
      {canAssign && selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
          padding: '10px 14px', borderRadius: 8,
          background: '#FEF3C7', border: '1px solid #F59E0B'
        }}>
          <strong style={{ color: '#92400E' }}>{selected.size} selected</strong>
          <button style={styles.btn} onClick={() => setAssignTarget({ bulk: true })}>
            Assign to Account Mgr →
          </button>
          <button style={styles.btnSecondary} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {isMobile ? (
        <div>
          {rows.length === 0 && (
            <div style={mobile.emptyHint}>
              {filter.dead ? 'No dead customers — nice work.' : 'No customers match these filters.'}
            </div>
          )}
          {rows.map(c => {
            const isSel = selected.has(c.id);
            const cardStyle = isSel
              ? { ...mobile.listItem, background: '#FEF3C7', borderColor: '#F59E0B' }
              : filter.dead
              ? { ...mobile.listItem, background: '#FEF7E6', borderColor: '#FCD34D' }
              : mobile.listItem;
            return (
              <div key={c.id} style={cardStyle}>
                <div style={mobile.listItemHeader}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                    {canAssign && (
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleRow(c.id)}
                        style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0 }}
                      />
                    )}
                    <Link to={`/accounts/${encodeURIComponent(c.name)}`} style={{ ...mobile.listItemTitle, color: colors.orange, textDecoration: 'none', wordBreak: 'break-word' }}>
                      {c.name}
                    </Link>
                  </div>
                  <RentalmanStatusPill status={c.status} />
                </div>
                {c.email && (
                  <div style={{ color: '#6B7280', fontSize: 12, marginTop: -4, marginBottom: 6, marginLeft: canAssign ? 30 : 0 }}>
                    {c.email}
                  </div>
                )}
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Acct #</span>
                  <span style={mobile.listValue}>{c.account_no || '—'}</span>
                </div>
                {showSpend && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>{c.spend_year} Spend</span>
                    <span style={mobile.listValue}><SpendCell c={c} /></span>
                  </div>
                )}
                <div style={{ ...mobile.listRow, flexDirection: 'column', alignItems: 'stretch' }}>
                  <span style={mobile.listLabel}>Account Mgr</span>
                  <div style={{ ...mobile.listValue, textAlign: 'left', marginTop: 4 }}>
                    <OwnerCell c={c} />
                  </div>
                </div>
                {(c.location_code || c.state) && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Loc · State</span>
                    <span style={mobile.listValue}>{c.location_code || '—'} · {c.state || '—'}</span>
                  </div>
                )}
                {c.phone && (
                  <div style={mobile.listRow}>
                    <span style={mobile.listLabel}>Phone</span>
                    <a href={`tel:${c.phone}`} style={{ ...mobile.listValue, color: colors.orange, textDecoration: 'none' }}>{c.phone}</a>
                  </div>
                )}
                <div style={mobile.listRow}>
                  <span style={mobile.listLabel}>Last CRM Activity</span>
                  <span style={mobile.listValue}>
                    {c.last_activity_at
                      ? fmtDate(c.last_activity_at)
                      : <span style={{ color: '#EF4444', fontWeight: 600 }}>Never</span>}
                  </span>
                </div>
                <div style={mobile.listActions}>
                  <Link to={`/customers/${c.id}`} style={{ ...styles.btnSecondary, ...mobile.btnTap, flex: 1, textAlign: 'center', textDecoration: 'none' }}>
                    📋 Reqs
                  </Link>
                  {canAssign && (
                    <button style={{ ...styles.btnSecondary, ...mobile.btnTap, flex: 1 }} onClick={() => setAssignTarget({ row: c })}>
                      {c.assigned_to_id ? 'Reassign' : 'Assign'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              {canAssign && (
                <th style={{ ...styles.th, width: 28 }}>
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={toggleAll}
                  />
                </th>
              )}
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Acct #</th>
              <th style={styles.th}>Account Mgr</th>
              {showSpend && (
                <th style={{ ...styles.th, textAlign: 'right' }} title="RentalMan invoiced $ (ex-tax) on this account, calendar year to date. Prior full year underneath.">
                  {rows[0]?.spend_year || new Date().getFullYear()} Spend
                </th>
              )}
              <th style={styles.th}>Loc</th>
              <th style={styles.th}>State</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>Last CRM Activity</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={(canAssign ? 10 : 9) + (showSpend ? 1 : 0)} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                {filter.dead ? 'No dead customers — nice work.' : 'No customers match these filters.'}
              </td></tr>
            )}
            {rows.map(c => (
              <tr key={c.id} style={
                selected.has(c.id) ? { background: '#FEF3C7' }
                : filter.dead ? { background: '#FEF7E6' } : {}
              }>
                {canAssign && (
                  <td style={styles.td}>
                    <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleRow(c.id)} />
                  </td>
                )}
                <td style={styles.td}><RentalmanStatusPill status={c.status} /></td>
                <td style={styles.td}>
                  <Link to={`/accounts/${encodeURIComponent(c.name)}`} style={{ color: colors.orange, fontWeight: 600, textDecoration: 'none' }}>
                    {c.name}
                  </Link>
                  {c.email && <div style={{ color: '#6B7280', fontSize: 11 }}>{c.email}</div>}
                </td>
                <td style={styles.td}>{c.account_no}</td>
                <td style={styles.td}>
                  <OwnerCell c={c} />
                </td>
                {showSpend && (
                  <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}><SpendCell c={c} /></td>
                )}
                <td style={styles.td}>{c.location_code || ''}</td>
                <td style={styles.td}>{c.state || ''}</td>
                <td style={styles.td}>{c.phone || ''}</td>
                <td style={styles.td}>
                  {c.last_activity_at
                    ? fmtDate(c.last_activity_at)
                    : <span style={{ color: '#EF4444', fontWeight: 600 }}>Never</span>}
                </td>
                <td style={styles.td}>
                  <Link to={`/customers/${c.id}`} style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', display: 'inline-block', textDecoration: 'none' }} title="Open site requirements (DISA, training, Safety Council, per diem, rate, badging form)">📋 Reqs</Link>
                  {canAssign && (
                    <>
                      {' '}
                      <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }} onClick={() => setAssignTarget({ row: c })}>
                        {c.assigned_to_id ? 'Reassign' : 'Assign'}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {assignTarget && (
        <AssignModal
          target={assignTarget}
          reps={reps}
          busy={busy}
          onCancel={() => setAssignTarget(null)}
          onSave={doAssign}
        />
      )}
    </>
  );
}

// -- Owner cell: prefers CRM assignment, falls back to Rentalman owner -------
function OwnerCell({ c }) {
  if (c.assigned_to_name) {
    return (
      <>
        <strong>{c.assigned_to_name}</strong>
        <div style={{ fontSize: 10, color: '#3730A3', fontWeight: 700, letterSpacing: 0.4 }}>
          ASSIGNED
        </div>
        {c.rentalman_owner_name && c.rentalman_owner_name !== c.assigned_to_name && (
          <div style={{ fontSize: 11, color: '#9CA3AF' }}>
            (Rentalman: {c.rentalman_owner_name})
          </div>
        )}
      </>
    );
  }
  if (c.rentalman_owner_name) {
    return (
      <>
        {c.rentalman_owner_name}
        <div style={{ fontSize: 10, color: '#9CA3AF', letterSpacing: 0.4 }}
          title={c.owner_source === 'invoices' ? 'Rep with the most RentalMan invoiced $ on this account in the last 12 months' : 'Sales rep on the RentalMan customer master'}>
          {c.owner_source === 'invoices' ? 'RM INVOICES' : 'RENTALMAN'}
        </div>
      </>
    );
  }
  if (c.sales_id) {
    return <span style={{ color: '#9CA3AF' }}>Sales ID {c.sales_id}</span>;
  }
  return <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Unassigned</span>;
}

// -- Spend cell: YTD invoiced $ with the prior full year underneath ---------
function SpendCell({ c }) {
  const ytd = c.spend_ytd == null ? null : Number(c.spend_ytd);
  const prior = c.spend_prior_year == null ? null : Number(c.spend_prior_year);
  return (
    <>
      {ytd == null
        ? <span style={{ color: '#9CA3AF' }}>—</span>
        : <strong>{fmtMoney(ytd)}</strong>}
      <div style={{ fontSize: 11, color: '#6B7280' }}>
        {c.spend_year - 1}: {prior == null ? '—' : fmtMoney(prior)}
      </div>
    </>
  );
}

// -- Assign modal -------------------------------------------------------------
function AssignModal({ target, reps, busy, onCancel, onSave }) {
  const single = !target.bulk;
  const [userId, setUserId] = useState(single ? (target.row.assigned_to_id || '') : '');
  const [notes, setNotes] = useState(single ? (target.row.assignment_notes || '') : '');

  const submit = (e) => {
    e?.preventDefault?.();
    onSave({ userId: userId === '' ? null : Number(userId), notes });
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
      }}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 20,
          // width, NOT minWidth: min-width beats max-width, so 420 overflowed
          // a 375px phone despite the 100vw clamp.
          width: 420, maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
        }}
      >
        <h2 style={{ ...styles.h2, marginTop: 0 }}>
          {target.bulk ? 'Assign Selected Customers' : `Assign · ${target.row.name}`}
        </h2>
        {!target.bulk && target.row.rentalman_owner_name && (
          <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
            Rentalman has this on <strong>{target.row.rentalman_owner_name}</strong> (Sales ID {target.row.sales_id || '—'}). The CRM assignment is a separate, chase-it directive that doesn't affect Rentalman.
          </div>
        )}

        <label style={styles.label}>Account Manager</label>
        <select
          style={{ ...styles.input, width: '100%' }}
          value={userId}
          onChange={e => setUserId(e.target.value)}
          autoFocus
        >
          <option value="">— Unassign —</option>
          {reps.map(r => (
            <option key={r.id} value={r.id}>{r.full_name}</option>
          ))}
        </select>

        <label style={{ ...styles.label, marginTop: 12 }}>Notes (optional)</label>
        <textarea
          style={{ ...styles.textarea, minHeight: 70 }}
          placeholder="e.g. Chase for Q2 turnaround opportunity"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" style={styles.btnSecondary} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" style={styles.btn} disabled={busy}>
            {busy ? 'Saving…' : (userId === '' ? 'Unassign' : 'Save Assignment')}
          </button>
        </div>
      </form>
    </div>
  );
}

const Stat = ({ label, value, sub }) => (
  <div style={styles.kpiCard}>
    <div style={styles.kpiLabel}>{label}</div>
    <div style={styles.kpiValue}>{value ?? 0}</div>
    {sub && <div style={styles.kpiSubtext}>{sub}</div>}
  </div>
);

// Rentalman uses single-letter status codes wrapped in parens. Pill colors:
//   A (Active)    = green
//   B             = gray  (no clear meaning -- treated as "other")
//   S (Suspended) = amber
//   D (Deleted)   = red
function RentalmanStatusPill({ status }) {
  if (!status) return <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>;
  const code = String(status).trim()[0]?.toUpperCase();
  const map = {
    A: { bg: '#D1FAE5', fg: '#065F46', label: 'Active' },
    B: { bg: '#F3F4F6', fg: '#374151', label: 'B' },
    S: { bg: '#FEF3C7', fg: '#92400E', label: 'Suspended' },
    D: { bg: '#FEE2E2', fg: '#991B1B', label: 'Deleted' }
  };
  const style = map[code] || { bg: '#F3F4F6', fg: '#374151', label: status };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 10,
      background: style.bg, color: style.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap'
    }}>
      {style.label}
    </span>
  );
}
